// Constants for our debate system
import { 
  LLM_FAMILIES,
  LLM_MODELS,
  MODEL_TO_FAMILY_MAP, 
  DEFAULT_MODELS,
  getLLMConfig,
  getAvailableModels,
  getDefaultModel
} from '../shared/models';

/**
 * @enum {string}
 */
export const DebateStatus = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  ERROR: 'error',
  DELETED: 'deleted'
};

/**
 * @enum {string}
 */
export const MessageRole = {
  SYSTEM: 'system',
  USER: 'user',
  ASSISTANT: 'assistant',
  PROTAGONIST: 'protagonist',
  ANTAGONIST: 'antagonist',
  MODERATOR: 'moderator',
  EVALUATOR: 'evaluator'
}

// Re-export LLM related constants from shared models
export { 
  LLM_FAMILIES,
  LLM_MODELS, 
  MODEL_TO_FAMILY_MAP, 
  DEFAULT_MODELS
};

// LLM display names and config - imported from shared models
export const LLM_CONFIG = getLLMConfig();

// Re-export helper functions
export { getAvailableModels, getDefaultModel };

/**
 * @typedef {Object} DebateTokenCounts
 * @property {number} input - Total input tokens
 * @property {number} output - Total output tokens
 * @property {number} total - Total tokens used (input + output)
 */

/**
 * @typedef {Object} DebateMessage
 * @property {string} role - One of the MessageRole values
 * @property {string} content - The message content
 * @property {DebateTokenCounts} [tokenCounts] - Optional token counts for this message
 * @property {{seconds: number, nanoseconds: number}} timestamp - Firestore timestamp
 */

/**
 * @typedef {Object} SharedDebate
 * @property {string} id - The shared debate ID
 * @property {string} originalDebateId - The ID of the original debate
 * @property {string} prompt - The initial debate prompt
 * @property {DebateMessage[]} uiMessages - Array of messages to display in the UI
 * @property {{seconds: number, nanoseconds: number}} createdAt - Firestore timestamp
 * @property {string} [summary] - Final debate summary from evaluator
 * @property {DebateTokenCounts} [tokenCounts] - Optional token counts for the entire debate
 */

/**
 * @typedef {Object} Debate
 * @property {string} id - The debate ID
 * @property {string} userId - The user's ID
 * @property {string[]} prompts - Array of prompts in chronological order
 * @property {string} status - One of the DebateStatus values
 * @property {string} [processingStatus] - Optional status message during processing
 * @property {{seconds: number, nanoseconds: number}} createdAt - Firestore timestamp
 * @property {{seconds: number, nanoseconds: number}} updatedAt - Firestore timestamp
 * @property {{seconds: number, nanoseconds: number}} [completedAt] - Optional completion timestamp
 * @property {DebateMessage[]} messages - Array of all debate messages in chronological order
 * @property {DebateMessage[]} llmMessages - Array of all debate messages in chronological order for llm
 * @property {DebateMessage[]} protagonistMessages - Array of protagonist messages
 * @property {DebateMessage[]} antagonistMessages - Array of antagonist messages
 * @property {DebateMessage[]} uiMessages - Array of messages to display in the UI
 * @property {string} [summary] - Final debate summary from evaluator
 * @property {string} [sharedId] - ID of the shared version of this debate, if it has been shared
 * @property {string} [model] - The specific LLM model used for this debate (e.g., 'gpt-4o', 'claude-3-7-sonnet')
 * @property {string} [displayName] - The display name for the debate/chat
 * @property {DebateTokenCounts} [tokenCounts] - Token counts for the entire debate
 */